import { useState } from 'react';
import {
  welcomeTopicConfigs,
  welcomeSteps,
  welcomeStats,
  welcomeRules,
  getStepsByTopic,
  getTotalOnboardingSeconds,
  type WelcomeTopic,
  type WelcomeFormat,
} from '@/mocks/rfwWelcome';

export default function BienvenidaSection() {
  const [activeTab, setActiveTab] = useState<'recorrido' | 'temas' | 'formato' | 'reglas'>('recorrido');
  const [selectedTopic, setSelectedTopic] = useState<WelcomeTopic | 'todas'>('todas');
  const [selectedFormat, setSelectedFormat] = useState<WelcomeFormat | 'todas'>('todas');

  const filteredSteps = (() => {
    let steps = welcomeSteps;
    if (selectedTopic !== 'todas') {
      steps = steps.filter((s) => s.topic === selectedTopic);
    }
    if (selectedFormat !== 'todas') {
      steps = steps.filter((s) => s.format === selectedFormat);
    }
    return steps.slice(0, 6);
  })();

  const totalSeconds = getTotalOnboardingSeconds();
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  const formatCounts = {
    paso: welcomeSteps.filter((s) => s.format === 'paso').length,
    video: welcomeSteps.filter((s) => s.format === 'video').length,
    guia: welcomeSteps.filter((s) => s.format === 'guia').length,
  };

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 border border-emerald-200 mb-4">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            <span className="text-xs font-semibold text-emerald-700 tracking-wide">SISTEMA DE BIENVENIDA</span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            ¿Nuevo en RFW? Esto es <span className="bg-gradient-to-r from-emerald-500 via-amber-500 to-rose-500 bg-clip-text text-transparent">para vos</span>
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
            En menos de 2 minutos vas a entender qué es RFW, cómo participar, cómo ganar puntos, cómo subir en el ranking y cómo entrar a clubes. Sin vueltas.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-8">
          {[
            { value: welcomeStats.totalNewUsersToday.toLocaleString(), label: 'Nuevos Hoy', icon: 'ri-user-add-line', color: 'text-emerald-600', bg: 'bg-emerald-50' },
            { value: `${welcomeStats.completionRate}%`, label: 'Completan', icon: 'ri-check-double-line', color: 'text-amber-600', bg: 'bg-amber-50' },
            { value: `${minutes}:${String(seconds).padStart(2, '0')}`, label: 'Duración Total', icon: 'ri-timer-line', color: 'text-rose-600', bg: 'bg-rose-50' },
            { value: welcomeSteps.length.toString(), label: 'Pasos Rápidos', icon: 'ri-footprint-line', color: 'text-violet-600', bg: 'bg-violet-50' },
          ].map((stat, i) => (
            <div key={i} className={`${stat.bg} rounded-lg p-3 text-center`}>
              <div className={`text-lg font-bold ${stat.color}`}>{stat.value}</div>
              <div className="flex items-center justify-center gap-1 text-xs text-foreground-500 mt-1">
                <i className={`${stat.icon} text-xs`}></i>
                <span>{stat.label}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center mb-7">
          <div className="inline-flex bg-background-100 rounded-full p-1">
            {(['recorrido', 'temas', 'formato', 'reglas'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  activeTab === tab
                    ? 'bg-background-50 text-foreground-950 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {tab === 'recorrido' && 'Recorrido'}
                {tab === 'temas' && 'Temas'}
                {tab === 'formato' && 'Formato'}
                {tab === 'reglas' && 'Reglas'}
              </button>
            ))}
          </div>
        </div>

        {/* Tab: Recorrido */}
        {activeTab === 'recorrido' && (
          <div>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-6">
              {/* Topic filter */}
              <div className="flex flex-wrap items-center justify-center gap-1.5">
                <span className="text-xs text-foreground-500 mr-1">Tema:</span>
                <button
                  onClick={() => setSelectedTopic('todas')}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all cursor-pointer ${
                    selectedTopic === 'todas' ? 'bg-emerald-500 text-white' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  Todos
                </button>
                {welcomeTopicConfigs.map((cfg) => (
                  <button
                    key={cfg.key}
                    onClick={() => setSelectedTopic(cfg.key)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all cursor-pointer flex items-center gap-1 ${
                      selectedTopic === cfg.key ? `${cfg.bgColor} ${cfg.color} border ${cfg.borderColor}` : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cfg.icon}`}></i>
                    {cfg.title}
                  </button>
                ))}
              </div>
              {/* Format filter */}
              <div className="flex flex-wrap items-center justify-center gap-1.5">
                <span className="text-xs text-foreground-500 mr-1">Formato:</span>
                {([
                  { key: 'todas', label: 'Todos', icon: 'ri-stack-line' },
                  { key: 'paso', label: 'Pasos', icon: 'ri-footprint-line' },
                  { key: 'video', label: 'Videos', icon: 'ri-play-circle-line' },
                  { key: 'guia', label: 'Guías', icon: 'ri-book-open-line' },
                ] as { key: string; label: string; icon: string }[]).map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setSelectedFormat(f.key as WelcomeFormat | 'todas')}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all cursor-pointer flex items-center gap-1 ${
                      selectedFormat === f.key ? 'bg-amber-500 text-white' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={f.icon}></i>
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Steps Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredSteps.map((step) => {
                const topicCfg = welcomeTopicConfigs.find((t) => t.key === step.topic);
                const formatIcon = step.format === 'video' ? 'ri-play-circle-fill' : step.format === 'guia' ? 'ri-book-open-fill' : 'ri-footprint-fill';
                const formatLabel = step.format === 'video' ? 'Video' : step.format === 'guia' ? 'Guía' : 'Paso rápido';
                const formatColor = step.format === 'video' ? 'text-rose-600 bg-rose-50' : step.format === 'guia' ? 'text-sky-600 bg-sky-50' : 'text-amber-600 bg-amber-50';

                return (
                  <div key={step.id} className={`bg-background-50 rounded-lg border ${topicCfg?.borderColor ?? 'border-background-200/70'} overflow-hidden hover:border-emerald-200 transition-colors`}>
                    {/* Visual */}
                    <div className="relative w-full h-40 overflow-hidden">
                      <img src={step.visualUrl} alt={step.title} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                      {/* Format badge */}
                      <div className={`absolute top-3 left-3 px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${formatColor}`}>
                        <i className={`${formatIcon} text-xs`}></i>
                        {formatLabel}
                      </div>
                      {/* Duration badge */}
                      <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-black/50 text-white text-xs font-medium">
                        {step.durationSeconds}s
                      </div>
                      {/* Topic badge at bottom */}
                      <div className="absolute bottom-3 left-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${topicCfg?.bgColor ?? 'bg-background-100'} ${topicCfg?.color ?? 'text-foreground-600'}`}>
                          {topicCfg?.title ?? step.topic}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-3">
                      <div className="flex items-center gap-2 mb-1.5">
                        <i className={`${step.icon} ${topicCfg?.color ?? 'text-foreground-500'} text-sm`}></i>
                        <span className="font-semibold text-sm text-foreground-950 truncate">{step.title}</span>
                      </div>
                      <p className="text-xs text-foreground-600 line-clamp-2 mb-3">{step.description}</p>
                      <a
                        href={step.ctaLink}
                        className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 hover:text-emerald-700 transition-colors cursor-pointer"
                      >
                        {step.ctaLabel}
                        <i className="ri-arrow-right-line text-xs"></i>
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Tab: Temas */}
        {activeTab === 'temas' && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
              {welcomeTopicConfigs.map((cfg, i) => {
                const topicSteps = getStepsByTopic(cfg.key);
                const topicDuration = topicSteps.reduce((s, st) => s + st.durationSeconds, 0);
                return (
                  <div key={cfg.key} className={`bg-background-50 rounded-lg p-4 border ${cfg.borderColor} text-center hover:border-emerald-200 transition-colors`}>
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <span className="text-xs font-bold text-foreground-400">#{i + 1}</span>
                      <div className={`w-10 h-10 rounded-xl ${cfg.bgColor} flex items-center justify-center`}>
                        <i className={`${cfg.icon} text-lg ${cfg.color}`}></i>
                      </div>
                    </div>
                    <div className={`font-semibold text-sm ${cfg.color} mb-1`}>{cfg.title}</div>
                    <div className="text-xs text-foreground-400 mb-3">{cfg.subtitle}</div>
                    <div className="text-xs text-foreground-600 leading-relaxed line-clamp-3 mb-3">{cfg.description}</div>
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      <div className="bg-emerald-50 rounded-md py-1.5">
                        <div className="font-bold text-emerald-600">{topicSteps.length}</div>
                        <div className="text-foreground-400">pasos</div>
                      </div>
                      <div className="bg-amber-50 rounded-md py-1.5">
                        <div className="font-bold text-amber-600">{topicDuration}s</div>
                        <div className="text-foreground-400">total</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Tab: Formato */}
        {activeTab === 'formato' && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Pasos rápidos */}
              <div className="bg-background-50 rounded-lg p-5 border border-amber-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
                    <i className="ri-footprint-line text-2xl text-amber-600"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-base text-foreground-950">Pasos Rápidos</div>
                    <div className="text-xs text-foreground-500">{formatCounts.paso} pasos · ~10-15s c/u</div>
                  </div>
                </div>
                <p className="text-sm text-foreground-600 mb-4">Lo esencial, directo al grano. Cada paso es una acción concreta que podés hacer ya mismo. Sin teoría, pura práctica.</p>
                <div className="space-y-2">
                  {welcomeSteps.filter((s) => s.format === 'paso').slice(0, 4).map((s) => {
                    const tCfg = welcomeTopicConfigs.find((t) => t.key === s.topic);
                    return (
                      <div key={s.id} className="flex items-center gap-2 text-xs">
                        <i className={`${s.icon} ${tCfg?.color ?? 'text-foreground-500'}`}></i>
                        <span className="text-foreground-700 truncate">{s.title}</span>
                        <span className="text-foreground-400 ml-auto">{s.durationSeconds}s</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Videos cortos */}
              <div className="bg-background-50 rounded-lg p-5 border border-rose-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-rose-50 flex items-center justify-center">
                    <i className="ri-play-circle-line text-2xl text-rose-600"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-base text-foreground-950">Videos Cortos</div>
                    <div className="text-xs text-foreground-500">{formatCounts.video} videos · 15-30s c/u</div>
                  </div>
                </div>
                <p className="text-sm text-foreground-600 mb-4">Para los que prefieren ver. Cada video muestra exactamente cómo hacer algo, sin relleno.</p>
                <div className="space-y-2">
                  {welcomeSteps.filter((s) => s.format === 'video').map((s) => {
                    const tCfg = welcomeTopicConfigs.find((t) => t.key === s.topic);
                    return (
                      <div key={s.id} className="flex items-center gap-2 text-xs">
                        <i className={`ri-play-circle-fill ${tCfg?.color ?? 'text-foreground-500'}`}></i>
                        <span className="text-foreground-700 truncate">{s.title}</span>
                        <span className="text-foreground-400 ml-auto">{s.durationSeconds}s</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Guías visuales */}
              <div className="bg-background-50 rounded-lg p-5 border border-sky-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-sky-50 flex items-center justify-center">
                    <i className="ri-book-open-line text-2xl text-sky-600"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-base text-foreground-950">Guías Visuales</div>
                    <div className="text-xs text-foreground-500">{formatCounts.guia} guías · ~10-12s c/u</div>
                  </div>
                </div>
                <p className="text-sm text-foreground-600 mb-4">Para los que quieren profundizar. Infografías, tablas y referencias visuales para entender cada tema a fondo.</p>
                <div className="space-y-2">
                  {welcomeSteps.filter((s) => s.format === 'guia').map((s) => {
                    const tCfg = welcomeTopicConfigs.find((t) => t.key === s.topic);
                    return (
                      <div key={s.id} className="flex items-center gap-2 text-xs">
                        <i className={`ri-book-open-fill ${tCfg?.color ?? 'text-foreground-500'}`}></i>
                        <span className="text-foreground-700 truncate">{s.title}</span>
                        <span className="text-foreground-400 ml-auto">{s.durationSeconds}s</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab: Reglas */}
        {activeTab === 'reglas' && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {welcomeRules.map((rule) => (
                <div key={rule.id} className="bg-background-50 rounded-lg p-5 border border-background-200/70">
                  <div className={`w-10 h-10 rounded-full ${rule.bgColor} flex items-center justify-center mb-3`}>
                    <i className={`${rule.icon} text-lg ${rule.color}`}></i>
                  </div>
                  <div className="font-semibold text-sm text-foreground-950 mb-2">{rule.title}</div>
                  <div className="text-xs text-foreground-600 leading-relaxed">{rule.description}</div>
                </div>
              ))}
            </div>

            {/* Topic distribution */}
            <div className="bg-background-100 rounded-lg p-4 border border-background-200/70">
              <div className="text-sm font-semibold text-foreground-950 mb-3">Distribución por Tema</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {welcomeTopicConfigs.map((cfg) => {
                  const topicSteps = getStepsByTopic(cfg.key);
                  const topicDuration = topicSteps.reduce((sum, s) => sum + s.durationSeconds, 0);
                  return (
                    <div key={cfg.key} className="text-center bg-background-50 rounded-lg p-3">
                      <i className={`${cfg.icon} ${cfg.color} text-lg mb-1 block`}></i>
                      <div className="text-lg font-bold text-foreground-950">{topicSteps.length}</div>
                      <div className="text-xs text-foreground-400">{cfg.title}</div>
                      <div className="text-xs text-foreground-300">{topicDuration}s</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="text-center mt-8">
          <a
            href="/bienvenida"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-white rounded-full text-sm font-semibold hover:bg-emerald-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-user-add-line"></i>
            Iniciar recorrido de bienvenida
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>

        {/* Final Rule */}
        <div className="mt-8 text-center">
          <p className="text-xs text-foreground-400 italic">Menos de 2 minutos.</p>
        </div>
      </div>
    </section>
  );
}